import {useLazyQuery} from "@apollo/client";
import {Divider, Paper, Stack, Typography} from "@mui/material";
import React from "react";
import {InversePentaSplit} from "../../../components/common/PentaSplit";
import {East, Email, Phone, Schedule, Send} from "@mui/icons-material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	Timeline,
	TimelineConnector,
	TimelineContent,
	TimelineDot,
	TimelineItem,
	timelineItemClasses,
	TimelineSeparator
} from "@mui/lab";
import {useParams} from "react-router-dom";
import {graphQLServices} from "../../../services/GraphQLServices";
import {GET_BATCH_ITEMS, GET_ERRORS, GET_EXTERNAL_CALLS, GET_ORDER, GET_ORDER_NOTIFICATIONS, GET_PRODUCTS_WITH_SUPPLIERS} from "../GraphQLQueries";
import {GeneralData} from "./GeneralData";
import {DetailsHeader} from "./DetailsHeader";
import {PolicyDetailsGrid} from "./PolicyDetailsGrid";
import {IOrderDetail} from "../models/IOrderDetail";
import {HealthData} from "./HealthData";
import {OrderDocuments} from "./OrderDocuments";
import {HouseBuildingData} from "./HouseBuildingData";
import {IOrderNotification, NotificationType} from "../models/IOrderNotification";
import {IBatchItem} from "../models/IBatchItem";
import {PolicyHolderGrid} from "./PolicyHolderGrid";
import {Localization} from "../../../localization/Localization";
import {OrderInformation} from "./OrderInformation";
import {EnumHelper} from "../../../libraries/util/EnumHelper";
import {IProduct} from "../models/IProduct";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {CurrentStageDisplay, HeaderSlot} from "../../../components/onsite/Footer";
import {IncentiveData} from "./IncentiveData";

// const Item = styled(Paper)(({theme}) => ({
// 	backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
// 	...theme.typography.body2,
// 	padding: theme.spacing(1),
// 	textAlign: "center",
// 	color: theme.palette.text.secondary,
// }));

function DetailsBody(props: { order: IOrderDetail | null; products: IProduct[]; errorFiles: string[]; externalFiles: string[] }) {
	if (!props.order)
		return (<div></div>);
	
	return (
		<div>
			<Accordion>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon/>}
					aria-controls="panel1a-content"
					id="panel1a-header"
				>
					<Typography>Order Information</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<OrderInformation order={props.order}/>
				</AccordionDetails>
			</Accordion>
			{props.order.policyId && <Accordion>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon/>}
					aria-controls="panel1a-content"
					id="panel1a-header"
				>
					<Typography>Transaction</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<GeneralData order={props.order}/>
				</AccordionDetails>
			</Accordion>}
			{props.order.policyId && <Accordion>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon/>}
					aria-controls="panel1a-content"
					id="panel1a-header"
				>
					<Typography>Incentive</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<IncentiveData order={props.order}/>
				</AccordionDetails>
			</Accordion>}
			{props.order.policyId && <Accordion>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon/>}
					aria-controls="panel1a-content"
					id="panel1a-header"
				>
					<Typography>Package/Policy</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<PolicyDetailsGrid order={props.order} products={props.products}/>
				</AccordionDetails>
			</Accordion>}
			
			{props.order.policyId && <Accordion>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon/>}
					aria-controls="panel1a-content"
					id="panel1a-header"
				>
					<Typography>Policy Holders</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<PolicyHolderGrid order={props.order}/>
				</AccordionDetails>
			</Accordion>}
			
			{props.order.policyId && props.order.productCategory?.toString() === "HEALTH_CARE" &&
				<Accordion key={"health"}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon/>}
						aria-controls="panel1a-content"
						id="panel1a-header"
					>
						<Typography>Health Care</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<HealthData order={props.order}/>
					</AccordionDetails>
				</Accordion>
			}
			{props.order.policyId && props.order.productCategory?.toString() === "HOME" &&
				<Accordion key={"home"}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon/>}
						aria-controls="panel1a-content"
						id="panel1a-header"
					>
						<Typography>Home Building</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<HouseBuildingData order={props.order}/>
					</AccordionDetails>
				</Accordion>
			}
			<Accordion>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon/>}
					aria-controls="panel1a-content"
					id="panel1a-header"
				>
					<Typography>Files</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<OrderDocuments order={props.order} errorFiles={props.errorFiles} externalFiles={props.externalFiles}/>
				</AccordionDetails>
			</Accordion>
		
		</div>
	);
}


function Text(props: { children: React.ReactNode }) {
	return <div style={{
		display: "flex",
		alignItems: "center",
		flexWrap: "wrap",
		gap: "5px",
	}}>
		{props.children}
	</div>;
}

function getTextForNotificationValue(notificationType: NotificationType, value: string): string {
	if (EnumHelper.normalizeHotchocolateEnum(notificationType) === NotificationType[NotificationType.UnderwritingChanged]) {
		switch (value) {
			case "V1":
				return "Approved";
			case "V2":
				return "Rejected";
			case "V3":
				return "In Review";
			default:
				return "";
		}
	}
	return value;
}

function getTextForNotificationKeyValue(notification: IOrderNotification): string {
	const key = Object.keys(notification.notificationValues).firstOrNull();
	if (key === null)
		return "";
	
	const value = notification.notificationValues[key];
	return key + " -> " + getTextForNotificationValue(notification.notificationType, value);
}

type TimelineEntry =
	| { type: "order"; date: string; order: IOrderDetail }
	| { type: "notification"; date: string; notification: IOrderNotification }
	| { type: "batchItem"; date: string; batchItem: IBatchItem };

function buildTimelineEntries(order: IOrderDetail, notifications: IOrderNotification[], batchItems: IBatchItem[]): TimelineEntry[] {
	const entries: TimelineEntry[] = [];

	if (order.transactionDate)
		entries.push({type: "order", date: order.transactionDate, order});

	for (const n of notifications)
		entries.push({type: "notification", date: n.creationDateTime, notification: n});

	for (const b of batchItems)
		entries.push({type: "batchItem", date: b.processDate, batchItem: b});

	entries.sort((a, b) => a.date.localeCompare(b.date));
	return entries;
}

function TimelineOverview(props: { order: IOrderDetail | null; orderNotifications: IOrderNotification[] | null; batchItems: IBatchItem[] | null }) {
	if (!props.order)
		return (<div></div>);

	const order = props.order;
	const mainApplicant = props.order.mainApplicant;
	const entries = buildTimelineEntries(order, props.orderNotifications ?? [], props.batchItems ?? []);

	return <Stack style={{
		maxWidth: "30%",
		padding: "10px",
	}}>
		<Typography
			variant={"h4"}>{mainApplicant?.prefixes ?? ""} {mainApplicant?.initials ?? ""} {mainApplicant?.callName ?? ""} {mainApplicant?.surname ?? ""}</Typography>

		{mainApplicant &&
			<>
				<p><Email/> {mainApplicant?.email}</p>
				<p><Phone/> {mainApplicant?.mobile}</p>
			</>
		}

		<Divider/>

		<Timeline
			sx={{
				[`& .${timelineItemClasses.root}:before`]: {
					flex: 0,
					padding: 0,
				},
			}}>
			{entries.map((entry) => {
				if (entry.type === "order") {
					return (
						<TimelineItem key={"order"}>
							<TimelineSeparator>
								<TimelineDot/>
								<TimelineConnector/>
							</TimelineSeparator>
							<TimelineContent>
								<Text>
									<Send/>
									<span>{Localization.formatDateTime(entry.order.transactionDate)}</span>
								</Text>
								<Text>Comparator: {entry.order.comparatorId} <East/>Orders <East/>{entry.order.id}</Text>
							</TimelineContent>
						</TimelineItem>
					);
				}
				if (entry.type === "notification") {
					const row = entry.notification;
					return (
						<TimelineItem key={row.id}>
							<TimelineSeparator>
								<TimelineDot/>
								<TimelineConnector/>
							</TimelineSeparator>
							<TimelineContent>
								<Text>
									<Send/>
									<span>{Localization.formatDateTime(row.creationDateTime)}</span>
								</Text>
								<Text>Notification<East/>{EnumHelper.normalizeHotchocolateEnum(row.notificationType)}<East/>{getTextForNotificationKeyValue(row)}
								</Text>
							</TimelineContent>
						</TimelineItem>
					);
				}
				const row = entry.batchItem;
				return (
					<TimelineItem key={row.id}>
						<TimelineSeparator>
							<TimelineDot/>
							<TimelineConnector/>
						</TimelineSeparator>
						<TimelineContent>
							<Text>
								<Schedule/>
								<span>{Localization.formatDateTime(row.processDate)}</span>
							</Text>
							<Text>BatchItem<East/>{EnumHelper.normalizeHotchocolateEnum(row.batchItemType)}<East/>{EnumHelper.normalizeHotchocolateEnum(row.status)}
							</Text>
							{row.completed && <Text>Completed: {Localization.formatDateTime(row.completed)}</Text>}
						</TimelineContent>
					</TimelineItem>
				);
			})}
		</Timeline>
	</Stack>;
}

// function OrderPackagePage() {
// 	if (!isAuthorizedByGroup("Order"))
// 		return unauthorizedPage();
// 	return OrderPackagePageContent();
// }

function OrderPackagePageContent() {
	const [orderDetail, setOrder] = React.useState<IOrderDetail | null>(null);
	const [orderNotifications, setOrderNotifications] = React.useState<IOrderNotification[] | null>(null);
	const [batchItems, setBatchItems] = React.useState<IBatchItem[] | null>(null);
	const [products, setProducts] = React.useState<IProduct[] | null>(null);
	const [productKrns, setProductKrns] = React.useState<string[]>([]);
	const [errorFiles, setErrorFiles] = React.useState<string[] | null>(null);
	const [externalFiles, setExternalFiles] = React.useState<string[] | null>(null);
	
	
	const {id} = useParams();
	const [, {
		refetch: refetchOrder
	}] = useLazyQuery(GET_ORDER, {
		client: graphQLServices.orderClient,
		variables: {id},
		onCompleted: (e) => {
			setOrder(e.orderDetails.edges[0].node);
			const krns: string[] = [];
			const order: IOrderDetail = e.orderDetails.edges[0].node;
			if (order.package?.customProperties?.productKrn)
				krns.push(order.package?.customProperties?.productKrn);
			
			if (order.package?.policyDetails) {
				for (let i = 0; i < order.package.policyDetails.length; i++) {
					const policy = order.package.policyDetails[i];
					if (policy.customProperties?.productKrn)
						krns.push(policy.customProperties?.productKrn);
				}
			}
			
			if (krns.length > 0) {
				setProductKrns(krns);
			}
		}
	});
	
	const [, {
		refetch: refetchOrderNotification
	}] = useLazyQuery(GET_ORDER_NOTIFICATIONS, {
		client: graphQLServices.orderClient,
		variables: {orderId: id},
		onCompleted: (e) => {
			const items: IOrderNotification[] = [];
			for (let i = 0; i < e.orderNotifications.edges.length; i++) {
				const element = e.orderNotifications.edges[i];
				items.push(element.node);
			}
			items.sort((a, b) => a.creationDateTime.localeCompare(b.creationDateTime));
			setOrderNotifications(items);
		}
	});

	const [, {
		refetch: refetchBatchItems
	}] = useLazyQuery(GET_BATCH_ITEMS, {
		client: graphQLServices.orderClient,
		variables: {orderId: id},
		onCompleted: (e) => {
			const items: IBatchItem[] = [];
			for (let i = 0; i < e.batchItems.edges.length; i++) {
				const element = e.batchItems.edges[i];
				items.push(element.node);
			}
			setBatchItems(items);
		}
	});
	
	const [loadProducts] = useLazyQuery(GET_PRODUCTS_WITH_SUPPLIERS, {
		client: graphQLServices.productsClient,
		variables: {productKrns, productCategory: orderDetail?.productCategory},
		onCompleted: (e) => {
			setProducts(e.productsWithSuppliers.products);
		}
	});
	
	const [, {
		refetch: refetchErrors
	}] = useLazyQuery(GET_ERRORS, {
		client: graphQLServices.orderClient,
		variables: {orderId: id},
		onCompleted: (e) => {
			const items: string[] = [];
			for (let i = 0; i < e.errors.length; i++) {
				const element = e.errors[i];
				items.push(element);
			}
			setErrorFiles(items);
		}
	});
	
	const [, {
		refetch: refetchExternalFiles
	}] = useLazyQuery(GET_EXTERNAL_CALLS, {
		client: graphQLServices.orderClient,
		variables: {orderId: id},
		onCompleted: (e) => {
			const items: string[] = [];
			for (let i = 0; i < e.externalCalls.length; i++) {
				const element = e.externalCalls[i];
				items.push(element);
			}
			setExternalFiles(items);
		}
	});
	
	React.useEffect(() => {
		if (orderDetail)
			return;
		// noinspection JSIgnoredPromiseFromCall
		refetchErrors();
	}, [refetchErrors, errorFiles]);
	
	React.useEffect(() => {
		if (orderDetail)
			return;
		// noinspection JSIgnoredPromiseFromCall
		refetchExternalFiles();
	}, [refetchExternalFiles, externalFiles]);
	
	React.useEffect(() => {
		if (orderDetail)
			return;
		// noinspection JSIgnoredPromiseFromCall
		refetchOrder();
	}, [refetchOrder, orderDetail]);
	
	React.useEffect(() => {
		if (orderNotifications)
			return;
		// noinspection JSIgnoredPromiseFromCall
		refetchOrderNotification();
	}, [refetchOrderNotification, orderNotifications]);

	React.useEffect(() => {
		if (batchItems)
			return;
		// noinspection JSIgnoredPromiseFromCall
		refetchBatchItems();
	}, [refetchBatchItems, batchItems]);
	
	React.useEffect(() => {
		if (products || productKrns.length === 0)
			return;
		// noinspection JSIgnoredPromiseFromCall
		loadProducts();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loadProducts, productKrns]);
	
	const div = (color: string, height: string | undefined = undefined) => <div style={{
		minWidth: "100px",
		minHeight: "100px",
		backgroundColor: color,
		height: height,
	}}><DetailsHeader order={orderDetail}/></div>;
	return <>
		<HeaderSlot><CurrentStageDisplay/></HeaderSlot>
		<Paper style={{
			height: "100%"
		}}>
			<InversePentaSplit
				top={div("rgb(25, 118, 210)")}
				center={<DetailsBody order={orderDetail} products={products ?? []} errorFiles={errorFiles ?? []} externalFiles={externalFiles ?? []}/>}
				right={<TimelineOverview order={orderDetail} orderNotifications={orderNotifications} batchItems={batchItems}/>}
				middle={Paper} // todo figure out how to make this happen
			/>
		</Paper>
	</>;
}

export const OrderPackagePage = pageAuthorizedByGroup(OrderPackagePageContent, ["Admin", "Order"]);
